/**
 * Renderer for the status column with inline edit support.
 *
 * Uses Quimby Popup + search (same pattern as InlineSelect) instead of Kendo DropDownList.
 * PUT to simpleUpdateInspectionPath on change.
 */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { TimeZoneContext } from '@contexts/timezone-context';
import api from '@utils/axios';
// eslint-disable-next-line import/no-unresolved
import { simpleUpdateInspectionPath } from '@routes';
import TooltipText from '@components/v2/TooltipText';
import { translate } from '@utils/i18n';
import { Popup } from '@ui/molecules/Popup';
import { Button } from '@ui/atoms/Button';
import { useInspectionsListingContext } from '../InspectionsListingContext';
function statusFromItem(row) {
    var _a, _b, _c;
    return {
        label: (_a = row.humanizedStatus) !== null && _a !== void 0 ? _a : '',
        value: row.status,
        icon: (_b = row.statusIcon) !== null && _b !== void 0 ? _b : undefined,
        color: (_c = row.statusColor) !== null && _c !== void 0 ? _c : undefined
    };
}
export function InspectionStatusRenderer({ item }) {
    const { statusesForSelect, onRowUpdate } = useInspectionsListingContext();
    useContext(TimeZoneContext);
    const { statusEditable, pendingIssues } = item;
    const blockedToEdit = !statusEditable || pendingIssues;
    const [edit, setEdit] = useState(false);
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState(() => statusFromItem(item));
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const saveInFlightRef = useRef(false);
    const [anchorEl, setAnchorEl] = useState(null);
    // Mass Update (and any listing refetch) replaces `item` without remounting the cell.
    // Skip while the inline editor is open or a save is in flight so a stale refetch
    // cannot overwrite the value the user just chose.
    useEffect(() => {
        if (edit || isSaving)
            return;
        setStatus(statusFromItem(item));
    }, [item.humanizedStatus, item.status, item.statusIcon, item.statusColor, edit, isSaving]);
    const filteredOptions = useMemo(() => {
        const q = search.trim().toLowerCase();
        const options = statusesForSelect;
        if (!q)
            return options;
        return options.filter((opt) => opt.label.toLowerCase().includes(q));
    }, [search, statusesForSelect]);
    const openEditor = () => {
        if (isSaving)
            return;
        setErrorMessage(null);
        setSearch('');
        setEdit(true);
    };
    const closeEditor = () => {
        if (isSaving)
            return;
        setEdit(false);
        setSearch('');
    };
    const onStatusChange = (next) => {
        if (saveInFlightRef.current)
            return;
        if (item.id == null)
            return;
        saveInFlightRef.current = true;
        setIsSaving(true);
        setErrorMessage(null);
        // js-routes optional `:locale` is the first path part. Passing
        // `simpleUpdateInspectionPath(id, { format: 'json' })` assigns `id` to
        // locale and throws "Route missing required keys: id". Use one params
        // object with `_options: true`, matching StatusCell.jsx.
        const updatePath = simpleUpdateInspectionPath({ id: item.id, format: 'json', _options: true });
        void api
            .put(updatePath, {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            inspection: { status_code: next.value }
        })
            .then(({ data }) => {
            var _a, _b, _c;
            onRowUpdate(data);
            setStatus({
                label: (_a = data.humanizedStatus) !== null && _a !== void 0 ? _a : '',
                value: data.status,
                icon: (_b = data.statusIcon) !== null && _b !== void 0 ? _b : undefined,
                color: (_c = data.statusColor) !== null && _c !== void 0 ? _c : undefined
            });
            setEdit(false);
            setSearch('');
        })
            .catch(() => {
            setErrorMessage(translate('error', { namespace: 'generic' }));
        })
            .finally(() => {
            saveInFlightRef.current = false;
            setIsSaving(false);
        });
    };
    const statusContent = (React.createElement("span", { style: { whiteSpace: 'nowrap' } },
        status.icon && status.color && React.createElement("i", { className: `fa-lg ${status.icon}`, style: { color: status.color } }),
        "\u00A0",
        status.label));
    const tooltip = pendingIssues
        ? translate('there_are_outstanding_inspection_assistant_issues', {
            namespace: 'features.inspections_index.table'
        })
        : translate('status_change_not_allowed', { namespace: 'features.inspections_index.table' });
    if (blockedToEdit) {
        return (React.createElement(TooltipText, { multiline: true, position: "top", text: tooltip }, statusContent));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement("button", { ref: setAnchorEl, type: "button", className: "qmb-control--text", onClick: openEditor, disabled: isSaving }, statusContent),
        React.createElement(Popup, { anchor: anchorEl, show: edit, onClose: closeEditor, position: "bottom-left", collision: { horizontal: 'fit', vertical: 'flip' }, containerClass: "qmb-ui-popup--action-list" },
            React.createElement(Popup.SearchInput, { value: search, onChange: setSearch, placeholder: translate('search', { namespace: 'generic' }) }),
            React.createElement(Popup.Section, { fillHeight: true },
                React.createElement(Popup.ActionList, null, filteredOptions.map((opt) => (React.createElement(Button, { key: opt.value, variant: "default", "aria-selected": opt.value === status.value, disabled: isSaving, onClick: () => onStatusChange(opt) },
                    opt.icon && opt.color && React.createElement("i", { className: `fa-lg ${opt.icon}`, style: { color: opt.color } }),
                    opt.label)))))),
        errorMessage && React.createElement("span", { role: "alert" }, errorMessage)));
}
